import React from 'react';

import { cssMap, cx } from '@compiled/react';

import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useProjectPicker } from '../contexts/PopupPickerContext';
import {
	CreateProjectButton,
	type CreateProjectButtonProps,
} from '../create-project-button/CreateProjectButton';
import type { UnlinkProjectButtonProps } from '../unlink-project-button/UnlinkProjectButton';

const styles = cssMap({
	container: {
		width: '360px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	content: {
		paddingBottom: token('space.075'),
	},
	title: {
		paddingTop: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.075'),
	},
	titleWithUnlinkButton: {
		paddingTop: token('space.075'),
	},
	createButton: {
		width: '100%',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	unlinkButton: {
		width: '100%',
		paddingTop: token('space.050'),
	},
});

export type ProjectPickerLayoutProps = {
	title?: string;
	createButton?: React.ReactElement<CreateProjectButtonProps>;
	unlinkButton?: React.ReactElement<UnlinkProjectButtonProps>;
	testId?: string;
	showCreateButton?: boolean;
};

export const ProjectPickerLayout = ({
	title,
	createButton,
	children,
	unlinkButton,
	testId,
	showCreateButton,
}: React.PropsWithChildren<ProjectPickerLayoutProps>) => {
	const { onCreateProject, hasProjectCreateAccess } = useProjectPicker();

	return (
		<Stack xcss={styles.container} testId={testId}>
			{unlinkButton && <Box xcss={styles.unlinkButton}>{unlinkButton}</Box>}
			{title && (
				<Box
					xcss={cx(styles.title, unlinkButton && styles.titleWithUnlinkButton)}
					role="heading"
					aria-level={6}
				>
					<Text color="color.text.subtlest" weight="bold" size="small">
						{title}
					</Text>
				</Box>
			)}
			<Box xcss={styles.content}>{children}</Box>
			{(hasProjectCreateAccess || showCreateButton) && (
				<Box xcss={styles.createButton}>
					{createButton ? createButton : <CreateProjectButton onClick={onCreateProject} />}
				</Box>
			)}
		</Stack>
	);
};
